/**
 * 主题服务（FR-029, 2026-07-11）
 *
 * 职责：
 *   1. 管理 Tactical（暗色）/ Day（亮色）双主题切换
 *   2. localStorage 持久化（键：battlecode-theme）
 *   3. dispatchEvent theme-change 自定义事件，通知 svgLoader / canvas-renderer 重建缓存
 *   4. 默认值：'tactical'（向后兼容）
 *
 * 约束：
 *   - 不引入新 npm 依赖
 *   - 纯浏览器 API（localStorage + dispatchEvent + CustomEvent）
 */

export type Theme = 'tactical' | 'day';

const STORAGE_KEY = 'battlecode-theme';
const EVENT_NAME = 'theme-change';

/** ThemeChangeEvent 携带切换后的主题值 */
export interface ThemeChangeDetail {
  theme: Theme;
  previousTheme: Theme;
}

let currentTheme: Theme = 'tactical';

/** 初始化：从 localStorage 读当前主题，同步设置 data-theme */
function init(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'day') {
      currentTheme = 'day';
    }
  } catch {
    // localStorage 不可用时保持战术默认
  }
  applyHtmlAttr(currentTheme);
  return currentTheme;
}

/** 设置 <html data-theme> 属性 */
function applyHtmlAttr(theme: Theme): void {
  if (theme === 'day') {
    document.documentElement.dataset.theme = 'day';
  } else {
    delete document.documentElement.dataset.theme;
  }
}

/** 获取当前主题 */
export function getTheme(): Theme {
  return currentTheme;
}

/** 切换主题并通知所有订阅者 */
export function setTheme(theme: Theme): void {
  const previous = currentTheme;
  if (theme === previous) return;

  currentTheme = theme;

  // 1. 持久化
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // ignore
  }

  // 2. 更新 DOM
  applyHtmlAttr(theme);

  // 3. 通知订阅者
  const detail: ThemeChangeDetail = { theme, previousTheme: previous };
  window.dispatchEvent(new CustomEvent<ThemeChangeDetail>(EVENT_NAME, { detail }));
}

/** 监听主题变更 */
export function onThemeChange(fn: (detail: ThemeChangeDetail) => void): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<ThemeChangeDetail>).detail;
    if (detail) fn(detail);
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

// 模块加载时初始化
init();
